import {useWarnAboutChange} from '@refinedev/core';
import React, {createContext, useContext, useState} from 'react';

import {UnsavedChangesModal} from './unsaved-changes-modal';
import {PromptCallbacks, PromptState, setGlobalPromptState} from './use-prompt';

const PromptContext = createContext<{
  openPrompt: (message: string, callbacks: PromptCallbacks) => void;
  closePrompt: () => void;
}>({
  openPrompt: () => {},
  closePrompt: () => {},
});

export const usePromptContext = () => useContext(PromptContext);

export const PromptProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const {warnWhen, setWarnWhen} = useWarnAboutChange();

  const [promptState, setPromptState] = useState<PromptState>({
    isOpen: false,
    message: '',
    callbacks: null,
  });

  const openPrompt = (message: string, callbacks: PromptCallbacks) => {
    setPromptState({
      isOpen: true,
      message,
      callbacks,
    });
    setWarnWhen?.(false);
  };

  const closePrompt = () => {
    setPromptState((prev) => ({
      ...prev,
      isOpen: false,
    }));
    setWarnWhen?.(false);
  };

  React.useEffect(() => {
    setGlobalPromptState({openPrompt});
  }, []);

  const handleConfirm = () => {
    if (promptState.callbacks?.onConfirm) {
      promptState.callbacks.onConfirm();
    }
    closePrompt();
  };

  const handleCancel = () => {
    if (promptState.callbacks?.onCancel) {
      promptState.callbacks.onCancel();
    }
    closePrompt();
  };

  return (
    <PromptContext.Provider value={{openPrompt, closePrompt}}>
      {children}
      <UnsavedChangesModal
        open={promptState.isOpen}
        message={promptState.message}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </PromptContext.Provider>
  );
};
